import React, { useEffect, useState } from 'react'
import { Button, Input, message, Modal, Select, Space, Tag, Tooltip } from 'antd'
import { SearchOutlined, PlusOutlined, UploadOutlined, EnvironmentOutlined, Loading3QuartersOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { LocationWrapper, SearchWrapper, TableWrapper, Wrapper } from './location.styled'
import { delLocations, fetchLocationTags, fetchLocations } from '../../apis/location'
import MainTable from '../../components/Basic/MainTable'
import useParams from '../../hook/useParams'
import dayjs from 'dayjs'
import Map from './components/Map'
import { useSelector } from 'react-redux'
import { fetchGroupList } from '../../apis/group'

function Location (props) {
  const { adminMode } = props
  const { t: ln_t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { pathname } = location || {}
  const user = useSelector((state) => state.user)
  const { nowBrand } = user || {}
  const params = useParams({ brandId: adminMode ? undefined : nowBrand })
  const { fetchParams, handleFetchParams } = params
  const apiFunc = async () => fetchLocations(fetchParams)
  const [locationInfo, setLocationInfo] = useState({})
  const [groupList, setGroupList] = useState([])
  const [searchType, setSearchType] = useState('name')
  const [tags, setTags] = useState([])

  const handleDelete = (id, data) => {
    Modal.confirm({
      title: `${ln_t('util.delete')} ${ln_t('sidebar.location')}`,
      content: `${ln_t('message.confirm.delete')} ${data.name}?`,
      onOk: async () => {
        const result = await delLocations(id)
        if (result?.status === 204) {
          handleFetchParams('add', {})
          message.success(ln_t('message.success.delete'))
        }
      }
    })
  }
  const checkingStatus = {
    0: { title: ln_t('location.notChecked') },
    1: { title: ln_t('location.checking'), color: 'geekblue', icon: <Loading3QuartersOutlined spin /> },
    2: { title: ln_t('location.success'), color: 'green' },
    3: { title: ln_t('location.error'), color: 'red', desc: ln_t('location.errDesc') }
  }
  const searchTypeOpt = [{ value: 'tag', label: ln_t('criteria.location.tag') }, { value: 'name', label: ln_t('criteria.location.name') }]

  useEffect(() => { }, [])
  const columns = [
    {
      title: ln_t('util.name'),
      dataIndex: 'name',
      sorter: true,
      render: (e, data) => <div onClick={() => setLocationInfo(data)} style={{ color: '#046bb4', cursor: 'pointer' }}>{e}</div>
    },
    {
      title: ln_t('location.checkingStatus'),
      dataIndex: 'geoTranslate',
      render: e => {
        const { title, icon, color, desc } = checkingStatus[e]
        return <Tooltip title={desc}><Tag style={{ width: '90px', cursor: 'default', textAlign: 'center' }} color={color} icon={icon}>{title}</Tag></Tooltip>
      }
    },
    { title: ln_t('util.status'), dataIndex: 'status', render: e => e === 1 ? ln_t('util.enable') : ln_t('util.disable') },
    { title: ln_t('util.updatedAt'), dataIndex: 'updatedAt', render: e => dayjs(e).format('DD-MM-YYYY') },
    {
      title: ln_t('util.action'),
      dataIndex: 'id',
      render: (id, data) => {
        const { longitude, latitude, radius, geoTranslate: geoStatus } = data
        const cantEdit = geoStatus === 0 || geoStatus === 1
        const canShow = longitude !== null && latitude !== null && radius
        return adminMode
          ? <div className="action-btn">
              <Tooltip title={canShow ? ln_t('location.viewLocation') : ln_t('location.noCoordinate')}>
                <Button size="small" disabled={!canShow} icon={<EnvironmentOutlined />} onClick={() => setLocationInfo(data)} />
              </Tooltip>
              <Tooltip title={cantEdit ? ln_t('location.cantView') : ''}>
                <Button size="small" disabled={cantEdit} icon={<EyeOutlined />} onClick={() => navigate(`${pathname}/detail`, { state: { data } })} />
              </Tooltip>
            </div>
          : <div className="action-btn">
              <Tooltip title={cantEdit ? ln_t('location.cantEdit') : ''}>
                <Button size="small" disabled={cantEdit} icon={<EditOutlined />} onClick={() => navigate(`${pathname}/edit`, { state: { data } })} />
              </Tooltip>
              <Tooltip title={canShow ? ln_t('location.viewLocation') : ln_t('location.noCoordinate')}>
                <Button size="small" disabled={!canShow} icon={<EnvironmentOutlined />} onClick={() => setLocationInfo(data)} />
              </Tooltip>
              <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(id, data)} />
            </div>
      }
    }
  ]

  const getGroups = async () => {
    const res = await fetchGroupList({ maxResults: 999 })
    if (res?.status === 200) setGroupList(res?.data?.map(i => ({ value: i.id, label: i.name })))
  }
  const getTags = async () => {
    const tag = await fetchLocationTags({ maxResults: 999, brandId: nowBrand })
    if (tag?.status === 200) setTags(tag?.data?.map(({ name }) => ({ value: name, label: name })))
  }
  useEffect(() => {
    if (adminMode) getGroups()
    getTags()
  }, [])

  useEffect(() => {
    const refetch = (nowBrand && nowBrand !== 0 && nowBrand !== fetchParams.brandId)
    if (refetch) handleFetchParams('add', { brandId: nowBrand })
  }, [nowBrand])

  return (
    <Wrapper>
      <LocationWrapper>
        <Map locationInfo={locationInfo} setLocationInfo={setLocationInfo} />
      </LocationWrapper>
      <TableWrapper>
        <SearchWrapper>
          <Space.Compact style={{ width: '50%' }}>
            <Select style={{ width: '100px' }} value={searchType} onChange={e => setSearchType(e)} options={searchTypeOpt}/>
            {searchType === 'tag'
              ? <>
                  <Select
                    showSearch
                    allowClear
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder={ln_t('util.search')}
                    options={tags}
                    optionFilterProp="children"
                    onChange={e => handleFetchParams('add', { searchType, searchKeyword: e?.join(',') })}
                    />
                  <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => handleFetchParams('add', {})} />
                </>
              : <Input.Search
                  allowClear
                  placeholder={ln_t('util.search')}
                  enterButton={<Button type="primary" size="small" icon={<SearchOutlined />} />}
                  onSearch={e => handleFetchParams('add', { searchType, searchKeyword: e })}
                  onKeyPress={e => { if (e.key === 'Enter') e.preventDefault() }} // to prevent submitting form on enter
                />
            }
          </Space.Compact>
          {adminMode
            ? <div className="d-flex">
                <div style={{ fontSize: '14px', fontWeight: 500, margin: 'auto 8px' }}>{ln_t('util.group')} :</div>
                <Select
                  showSearch
                  style={{ minWidth: '120px' }}
                  defaultValue="all"
                  placeholder={`${ln_t('util.select')} ${ln_t('util.group')}`}
                  options={[{ value: 'all', label: 'ALL' }, ...groupList]}
                  optionFilterProp="children"
                  onChange={e => handleFetchParams('add', { groupId: e === 'all' ? undefined : e })}
                />
              </div>
            : <div style={{ display: 'flex', gap: '6px' }}>
                <Button type="default" icon={<UploadOutlined />} onClick={() => navigate(`${pathname}/upload`)}>{ln_t('location.upload')}</Button>
                <Button type="default" icon={<PlusOutlined />} onClick={() => navigate(`${pathname}/new`)}>{ln_t('util.add')}</Button>
              </div>
          }
        </SearchWrapper>
        <MainTable
          columns={columns}
          rowKey="id"
          apiFunc={apiFunc}
          params={params}
        />
      </TableWrapper>
    </Wrapper>
  )
}

export default Location
